/* eslint-disable no-param-reassign */
import React, { useState, useEffect, useCallback } from 'react';

import { format, parseISO } from 'date-fns';

import { Button, Row, Col, Badge } from 'react-bootstrap';
import { FaIdCard, FaPencilAlt } from 'react-icons/fa';

import { Link } from 'react-router-dom';
import PacienteCadastro from '../PacienteCadastro';
import Modal from '../Modal';

import { formataCPF, formataTelefone, formataIdade } from '../../utils/funcoes';

import { CartaoSaude, Container, PacienteContainer } from './styles';
import { IPaciente } from '../Agendamento';
import { IProcedimentoAgendamentoAlvaro } from '../../pages/PacienteAtendimentoAlvaro';
import getResponseErrors from '../../utils/getResponseErrors';
import { useToast } from '../../hooks/toast';
import { useClinica } from '../../hooks/clinica';

export type IPacienteFormatado = IPaciente;

export interface IInfoCartao {
  possuiCartao: boolean;
  PlaCodigo?: number;
  Status?: string;
  parcelas?: string;
  PlaContratoAssinado?: boolean;
}

type PacienteProps = {
  pacienteID?: number;
  paciente: IPacienteFormatado;
  procedimentos?: IProcedimentoAgendamentoAlvaro[];
  cartao?: IInfoCartao;
  exibeEditar?: boolean;
  exibeInfoCartao?: boolean;
  idadeCompleta?: boolean;
  handleAlterouDados?(dados: IPaciente): void;
};

const PacienteCabecalho: React.FC<PacienteProps> = ({
  paciente,
  pacienteID,
  exibeEditar,
  cartao,
  exibeInfoCartao,
  idadeCompleta = false,
  procedimentos,
  handleAlterouDados,
}) => {
  const { addToast } = useToast();
  const { imagensClinica } = useClinica();

  const semFoto =
    imagensClinica.find(img => img.nome === 'semFoto')?.arquivo_url || '';

  const [pacienteFormatado, setPacienteFormatado] = useState<IPaciente>();
  const [nomeProcedimentos, setNomeProcedimentos] = useState<
    string | undefined
  >(undefined);
  const [showModalPacienteCadastro, setShowModalPacienteCadastro] = useState(
    false,
  );

  useEffect(() => {
    const dataNasc = parseISO(paciente.PacDataNasc.toString());
    setPacienteFormatado({
      ...paciente,
      PacTelefone: formataTelefone(paciente.PacTelefone),
      PacCelular: formataTelefone(paciente.PacCelular),
      PacCPF:
        paciente.PacCPF && paciente.PacCPF.length >= 11
          ? formataCPF(paciente.PacCPF)
          : 'Não cadastrado',
      PacDataNascFormatada: format(dataNasc, 'dd/MM/yyyy'),
      PacIdade: formataIdade(dataNasc, idadeCompleta),
      avatar_url:
        paciente.avatar_url && paciente.avatar_url !== ''
          ? paciente.avatar_url
          : semFoto,
    });
  }, [paciente, idadeCompleta, semFoto]);

  useEffect(() => {
    if (procedimentos) {
      const nomes = procedimentos.map(proc => proc.ProNome).join(',');
      setNomeProcedimentos(nomes);
    }
  }, [procedimentos]);

  const handleAtualizouDadosPaciente = useCallback(
    (dados: IPaciente) => {
      if (dados) {
        try {
          const dataNasc = new Date(dados.PacDataNasc.toString());
          setPacienteFormatado({
            ...dados,
            PacNome: dados.PacNome,
            PacEmail: dados.PacEmail,
            PacTelefone: formataTelefone(dados.PacTelefone),
            PacCelular: formataTelefone(dados.PacCelular),
            PacCPF:
              dados.PacCPF && dados.PacCPF.length === 11
                ? formataCPF(dados.PacCPF)
                : 'Não cadastrado',
            PacDataNascFormatada: format(dataNasc, 'dd/MM/yyyy'),
            PacIdade: formataIdade(dataNasc, idadeCompleta),
            avatar_url:
              dados.avatar_url && dados.avatar_url !== ''
                ? dados.avatar_url
                : semFoto,
          });
          if (handleAlterouDados) handleAlterouDados(dados);
          setShowModalPacienteCadastro(false);
        } catch (error) {
          const mensagemErro = getResponseErrors(error);
          addToast({
            title: 'Erro ao atualizar state dados paciente',
            type: 'error',
            description: mensagemErro,
          });
        }
      }
    },
    [idadeCompleta, semFoto, addToast, handleAlterouDados],
  );

  return (
    <Container>
      {showModalPacienteCadastro && paciente && (
        <Modal
          show={showModalPacienteCadastro}
          titulo="Cadastro do Paciente"
          handleFecharModal={() => {
            setShowModalPacienteCadastro(false);
          }}
        >
          <PacienteCadastro
            exibeMenu={false}
            pacCodigo={pacienteID}
            handleRetornaDados={
              dadosAtualizados => handleAtualizouDadosPaciente(dadosAtualizados)
              // eslint-disable-next-line react/jsx-curly-newline
            }
          />
        </Modal>
      )}

      {pacienteFormatado && (
        <Row>
          <Col md={9} xl={9}>
            <PacienteContainer>
              <img
                src={pacienteFormatado.avatar_url}
                alt={pacienteFormatado.PacNome}
              />
              <div>
                {pacienteFormatado.PacNomeSocial &&
                pacienteFormatado.PacNomeSocial?.trim() !== '' ? (
                  <>
                    <small>Nome Social: </small>
                    <span>
                      <b>
                        {pacienteFormatado.PacNomeSocial} -{' '}
                        {pacienteFormatado.PacNome}
                      </b>
                    </span>
                  </>
                ) : (
                  <span>
                    <b>{pacienteFormatado.PacNome}</b>
                  </span>
                )}
                {exibeEditar && (
                  <Button
                    type="button"
                    variant="outline-primary"
                    style={{
                      paddingTop: '0.1rem',
                      paddingBottom: '0.1rem',
                      height: '19px',
                      lineHeight: '0.5',
                    }}
                    size="sm"
                    onClick={() => setShowModalPacienteCadastro(true)}
                  >
                    <FaPencilAlt size={12} />
                  </Button>
                )}
                <br />
                <span>
                  Data de Nascimento:{' '}
                  <b>{pacienteFormatado.PacDataNascFormatada}</b>
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Idade:&nbsp;
                  <b>{pacienteFormatado.PacIdade}</b>
                </span>
                <br />
                <span>
                  CPF: <b>{pacienteFormatado.PacCPF}</b>
                </span>
                {!procedimentos && (
                  <span>
                    Telefone:{' '}
                    <b>
                      {pacienteFormatado?.PacCelular}{' '}
                      {pacienteFormatado?.PacTelefone}
                    </b>
                  </span>
                )}
                {procedimentos && procedimentos.length > 0 && (
                  <span>
                    Procedimentos: <b>{nomeProcedimentos}</b>
                  </span>
                )}
              </div>
            </PacienteContainer>
          </Col>
          <Col md={3} xl={3} className={`${exibeInfoCartao ? '' : 'd-none'}`}>
            <b style={{ display: 'block' }}>Convênio</b>
            {cartao && cartao.possuiCartao ? (
              <>
                <CartaoSaude emDia={cartao.Status === 'Em Dia'}>
                  <Link
                    to={`/cartaosaudedetalhe/${cartao.PlaCodigo}`}
                    target="_blank"
                  >
                    <FaIdCard size={16} /> Cartão Saúde {cartao.Status}
                  </Link>
                </CartaoSaude>
                {!cartao.PlaContratoAssinado && (
                  <div
                    style={{
                      height: '10px',
                      fontSize: '13px',
                      textAlign: 'center',
                    }}
                  >
                    <Badge pill variant="warning">
                      Assinar Contrato
                    </Badge>
                  </div>
                )}
              </>
            ) : (
              <p style={{ marginTop: '11px', marginBottom: 0 }}>Particular</p>
            )}
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default PacienteCabecalho;
